/**
 * Карточка хозяйства: имена/алиасы, направления (с сырой меткой источника),
 * землепользования, размещение-2026 и происхождение.
 *
 * Меры размещения помечены неаддитивными: сервер объявляет это явно, а экран
 * не строит по ним ни одной суммы.
 */

import { Link, useLocation, useParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { api } from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { PageHeader } from "../../components/AppShell";
import { Disclosure } from "../../components/Disclosure";
import { districtStep, LevelTrail, type TrailStep } from "../../components/LevelTrail";
import { LevelPocket } from "../ai/LevelPocket";
import { AreaProgress, AxisLegend, DoneSplit } from "../../components/Progress";
import {
  Area,
  Badge,
  Button,
  Card,
  Count,
  DataStatePanel,
  DefinitionList,
  EvidenceBadge,
  InlineAlert,
  ProvenanceLine,
  RequestErrorPanel,
  Text,
} from "../../components/ui";
import { actorTypeLabel, identityLabel, scopeLabel } from "../../lib/epistemic";
import { DemoCropTimeline } from "../placement/DemoCropTimeline";
import { DemoPlacementDetail } from "../placement/DemoPlacementDetail";
import { formatArea, formatCount } from "../../lib/format";
import { levelPath } from "../../lib/levels";
import { ROUTES } from "../../lib/routes";
import { actorMassifLoad, actorMassifs, nounForms } from "./actorModel";
import styles from "../features.module.css";
import { WORDS } from "../../astra/lib/format";
import { COMMON } from "../../i18n/common";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";

const m = defineMessages({
  loading: { id: 'actors.detail.loading', defaultMessage: 'Загружаем карточку хозяйства', description: 'Заголовок заглушки загрузки карточки хозяйства' },
  loadFailed: { id: 'actors.detail.loadFailed', defaultMessage: 'Карточка не загрузилась', description: 'Заголовок панели ошибки загрузки карточки хозяйства' },
  notFound: { id: 'actors.detail.notFound', defaultMessage: 'Такого субъекта нет', description: 'Заголовок панели, когда сервер ответил «не найдено» на адрес карточки хозяйства' },
  toRegistry: { id: 'actors.detail.toRegistry', defaultMessage: 'К реестру хозяйств', description: 'Кнопка-ссылка в шапке карточки хозяйства и в панели ошибки: вернуться в реестр хозяйств' },
  massifsCaption: { id: 'actors.detail.trail.massifs', defaultMessage: 'Массивы', description: 'Подпись звена цепочки уровней, когда земля хозяйства лежит в нескольких массивах; рядом число массивов' },
  unnamed: { id: 'actors.detail.unnamed', defaultMessage: 'Субъект #{id}', description: 'Заголовок карточки и звено цепочки, когда у субъекта нет имени; {id} — номер записи' },
  actionWaits: { id: 'actors.detail.action.waits', defaultMessage: 'Что от него ждут', description: 'Кнопка в шапке карточки хозяйства: перейти к работам этого хозяйства («Мои работы» за хозяйство)' },
  actionMap: { id: 'actors.detail.action.map', defaultMessage: 'Земля на карте', description: 'Кнопка в шапке карточки хозяйства: показать землю хозяйства на карте' },
  actionVerify: { id: 'actors.detail.action.verify', defaultMessage: 'Проверка работ', description: 'Кнопка в шапке карточки хозяйства: очередь проверки работ этого хозяйства (инспектор, менеджер)' },
  whereLand: { id: 'actors.detail.whereLand', defaultMessage: 'Где земля хозяйства', description: 'Подпись над чипами массивов: в каких массивах лежит земля хозяйства' },
  chip: { id: 'actors.detail.chip', defaultMessage: '{massif} · {contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} · {area} га', description: 'Чип-ссылка на массив в карточке хозяйства; {massif} — имя массива, {area} — площадь хозяйства там, гектары (число)' },
  chipMissing: { id: 'actors.detail.chipMissing', defaultMessage: '{massif} · {contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} · {area} га · без величины {missing, plural, one {# строка} few {# строки} many {# строк} other {# строки}}', description: 'Чип-ссылка на массив, когда у части строк землепользования нет площади; {missing} — число таких строк, их площадь не считается нулём' },
  doneTitle: { id: 'actors.detail.done.title', defaultMessage: 'Что сделано, а что нет', description: 'Заголовок главной карточки-ответа на уровне хозяйства: отчётность по работам на полях' },
  absentLoading: { id: 'actors.detail.done.absentLoading', defaultMessage: 'очередь работ ещё загружается', description: 'Пояснение вместо доли «отчитались», пока загружается очередь работ' },
  absentNone: { id: 'actors.detail.done.absentNone', defaultMessage: 'работ этому хозяйству в текущем срезе не назначено: считать долю не от чего', description: 'Пояснение вместо доли «отчитались», когда хозяйству работ не поручали' },
  land: { id: 'actors.detail.land', defaultMessage: 'Земля', description: 'Подпись KPI на уровне хозяйства: вся земля хозяйства, гектары' },
  landArable: { id: 'actors.detail.land.arable', defaultMessage: 'пашня:', description: 'Мелкая подпись под KPI «Земля»; сразу за ней идёт число — площадь пашни' },
  contours: { id: 'actors.detail.contours', defaultMessage: 'Контуры', description: 'Подпись KPI на уровне хозяйства: число контуров' },
  landUsesCount: { id: 'actors.detail.contours.landUses', defaultMessage: 'землепользований: {count}', description: 'Мелкая подпись под KPI «Контуры»; {count} — число строк землепользования' },
  works: { id: 'actors.detail.works', defaultMessage: 'Работы на полях', description: 'Подпись KPI на уровне хозяйства: сколько работ на полях поручено хозяйству в текущем срезе' },
  worksAssigned: { id: 'actors.detail.works.assigned', defaultMessage: 'поручено в текущем срезе', description: 'Мелкая подпись под KPI «Работы на полях», когда число получено' },
  worksFailed: { id: 'actors.detail.works.failed', defaultMessage: 'очередь работ не загрузилась', description: 'Мелкая подпись под KPI «Работы на полях», когда запрос очереди не удался' },
  noReport: { id: 'actors.detail.noReport', defaultMessage: 'Без отчёта сейчас', description: 'Подпись KPI на уровне хозяйства: сколько работ на полях пока без отчёта' },
  noReportNote: { id: 'actors.detail.noReport.note', defaultMessage: 'отчёта не поступало: это отсутствие данных, а не подтверждённое невыполнение', description: 'Мелкая подпись под KPI «Без отчёта сейчас»: пояснение, что это не невыполнение' },
  contoursAndHa: { id: 'actors.detail.contoursHa.title', defaultMessage: 'Контуры и гектары работ', description: 'Заголовок раскрывающегося блока на уровне хозяйства: контуры и га работ на полях' },
  awaiting: { id: 'actors.detail.awaiting', defaultMessage: 'ждут суждения проверяющего: {count, plural, one {# работа на поле} few {# работы на полях} many {# работ на полях} other {# работы на полях}}', description: 'Краткая строка свёрнутого блока: сколько работ на полях ждёт вердикта проверяющего' },
  awaitingUnknown: { id: 'actors.detail.awaiting.unknown', defaultMessage: 'ждут суждения проверяющего: нет данных', description: 'Краткая строка свёрнутого блока, когда число работ в ожидании вердикта неизвестно' },
  awaitingMissing: { id: 'actors.detail.awaiting.missing', defaultMessage: ' · без величины {missing} из {rows}', description: 'Добавка к краткой строке блока; {missing} — строк без величины, {rows} — всего строк; начинается с разделителя' },
  contoursWithWorks: { id: 'actors.detail.contoursWithWorks', defaultMessage: 'Контуры с работами', description: 'Подпись над долей: контуры хозяйства, которых коснулись работы (не вся земля)' },
  worksNote: { id: 'actors.detail.worksNote', defaultMessage: 'Работы разбираются на своих экранах — ссылки на них в шапке.', description: 'Примечание в раскрытом блоке: где разбирать сами работы' },
  directionsTitle: { id: 'actors.detail.directions.title', defaultMessage: 'Направления хозяйства', description: 'Заголовок карточки со списком направлений деятельности хозяйства' },
  directionsEmptyTitle: { id: 'actors.detail.directions.emptyTitle', defaultMessage: 'Направления не заявлены', description: 'Заголовок пустого состояния карточки направлений' },
  directionsEmpty: { id: 'actors.detail.directions.empty', defaultMessage: 'В источнике для этого субъекта нет строк направления.', description: 'Пояснение в пустом состоянии карточки направлений' },
  directionsCaption: { id: 'actors.detail.directions.caption', defaultMessage: 'Направление — фокус хозяйства, а НЕ список разрешённых культур. Составная метка источника раскладывается на атомарные коды, но сама метка сохраняется дословно.', description: 'Подпись таблицы направлений (для скринридера и под таблицей): что такое направление' },
  colAtomCode: { id: 'actors.detail.directions.col.code', defaultMessage: 'Атомарный код', description: 'Заголовок колонки таблицы направлений: код направления после разбора составной метки' },
  colSourceLabel: { id: 'actors.detail.directions.col.label', defaultMessage: 'Метка источника', description: 'Заголовок колонки таблицы направлений: исходная метка из файла-источника дословно' },
  colLayer: { id: 'actors.detail.directions.col.layer', defaultMessage: 'Слой', description: 'Заголовок колонки таблицы направлений: слой источника' },
  colAsserted: { id: 'actors.detail.directions.col.asserted', defaultMessage: 'Заявлено раз', description: 'Заголовок числовой колонки: сколько раз источник заявил это направление' },
  colClass: { id: 'actors.detail.col.class', defaultMessage: 'Класс', description: 'Заголовок колонки с отметкой класса доказательства (факт, заявлено, расчёт…)' },
  cropBadge: { id: 'actors.detail.directions.crop', defaultMessage: 'культура: {crop}', description: 'Бейдж рядом с кодом направления; {crop} — код культуры' },
  landUsesTitle: { id: 'actors.detail.landUses.title', defaultMessage: 'Землепользования', description: 'Заголовок раскрывающегося блока: строки землепользования хозяйства' },
  landUsesNone: { id: 'actors.detail.landUses.none', defaultMessage: 'действующих землепользований в этом срезе нет', description: 'Краткая строка свёрнутого блока землепользований, когда их нет' },
  landUsesSummary: { id: 'actors.detail.landUses.summary', defaultMessage: '{rows, plural, one {# строка} few {# строки} many {# строк} other {# строки}} на {massifs, plural, one {# массиве} other {# массивах}}', description: 'Краткая строка свёрнутого блока землепользований: сколько строк и на скольких массивах' },
  landUsesMore: { id: 'actors.detail.landUses.more', defaultMessage: 'показать {count}', description: 'Кнопка раскрытия блока землепользований; {count} — число строк' },
  landUsesEmptyTitle: { id: 'actors.detail.landUses.emptyTitle', defaultMessage: 'Земель за субъектом не числится', description: 'Заголовок пустого состояния блока землепользований' },
  landUsesEmpty: { id: 'actors.detail.landUses.empty', defaultMessage: 'В текущем контексте анализа действующих землепользований нет.', description: 'Пояснение в пустом состоянии блока землепользований' },
  landUsesCaption: { id: 'actors.detail.landUses.caption', defaultMessage: 'Площадь показана на зерне самого утверждения: строка на часть контура несёт площадь части, строка на контур — площадь контура. Складывать их между собой можно: каждая учтена раз.', description: 'Подпись таблицы землепользований: чья площадь в строке и можно ли складывать' },
  colContour: { id: 'actors.detail.landUses.col.contour', defaultMessage: 'Контур', description: 'Заголовок колонки таблицы землепользований: номер контура (ссылка)' },
  colMassif: { id: 'actors.detail.landUses.col.massif', defaultMessage: 'Массив', description: 'Заголовок колонки таблицы землепользований: массив, где лежит контур (ссылка)' },
  colPart: { id: 'actors.detail.landUses.col.part', defaultMessage: 'Часть', description: 'Заголовок колонки таблицы землепользований: часть контура' },
  wholeContour: { id: 'actors.detail.landUses.wholeContour', defaultMessage: 'контур целиком', description: 'Значение колонки «Часть», когда землепользование относится ко всему контуру' },
  colArable: { id: 'actors.detail.col.arable', defaultMessage: 'Пашня, га', description: 'Заголовок числовой колонки: площадь пашни, гектары' },
  colTotal: { id: 'actors.detail.col.total', defaultMessage: 'Всего, га', description: 'Заголовок числовой колонки: общая площадь, гектары' },
  placementTitle: { id: 'actors.detail.placement.title', defaultMessage: 'Размещение культур, сезон {season}', description: 'Заголовок карточки размещения культур хозяйства; {season} — сезон (год)' },
  nonAdditive: { id: 'actors.detail.placement.nonAdditive', defaultMessage: 'Меры неаддитивны — суммировать нельзя', description: 'Заголовок предупреждения над таблицей размещения: разные меры одной записи складывать нельзя' },
  placementCaption: { id: 'actors.detail.placement.caption', defaultMessage: 'Одна строка источника — одно размещение; величины внутри неё относятся к разным метрикам одного факта.', description: 'Подпись таблицы размещения: строка источника и метрики внутри' },
  colMetric: { id: 'actors.detail.placement.col.metric', defaultMessage: 'Метрика', description: 'Заголовок колонки таблицы размещения: код метрики' },
  colAreaHa: { id: 'actors.detail.placement.col.area', defaultMessage: 'Площадь, га', description: 'Заголовок числовой колонки таблицы размещения: площадь, гектары' },
  colSource: { id: 'actors.detail.placement.col.source', defaultMessage: 'Источник', description: 'Заголовок колонки таблицы размещения: откуда взята запись' },
  placementCard: { id: 'actors.detail.placementEmpty.title', defaultMessage: 'Размещение культур', description: 'Заголовок карточки размещения, когда строк нет' },
  placementEmptyTitle: { id: 'actors.detail.placementEmpty.stateTitle', defaultMessage: 'Строк размещения нет', description: 'Заголовок пустого состояния карточки размещения' },
  placementEmpty: { id: 'actors.detail.placementEmpty.text', defaultMessage: 'Для этого субъекта в реестре нет ни одной строки размещения. Это отсутствие строки, а не нулевая площадь.', description: 'Пояснение в пустом состоянии карточки размещения: нет строки — не ноль' },
  identityTitle: { id: 'actors.detail.identity.title', defaultMessage: 'Идентичность', description: 'Заголовок карточки: тип, статус и область видимости субъекта' },
  identityKind: { id: 'actors.detail.identity.kind', defaultMessage: 'Тип субъекта', description: 'Термин в списке карточки «Идентичность»: вид субъекта (хозяйство, физлицо…)' },
  identityStatus: { id: 'actors.detail.identity.status', defaultMessage: 'Статус идентичности', description: 'Термин в списке карточки «Идентичность»: насколько надёжно опознан субъект' },
  identityScope: { id: 'actors.detail.identity.scope', defaultMessage: 'Область видимости', description: 'Термин в списке карточки «Идентичность»: фермерские, дехканские, контекст' },
  farmLike: { id: 'actors.detail.identity.farmLike', defaultMessage: 'Похоже на хозяйство', description: 'Термин в списке карточки «Идентичность»: признак, что субъект выглядит как хозяйство' },
  yes: { id: 'actors.detail.yes', defaultMessage: 'да', description: 'Значение «да» в списке карточки' },
  no: { id: 'actors.detail.no', defaultMessage: 'нет', description: 'Значение «нет» в списке карточки' },
  namesTitle: { id: 'actors.detail.names.title', defaultMessage: 'Имена и алиасы ({count})', description: 'Заголовок карточки имён субъекта; {count} — сколько имён' },
  nameUnknown: { id: 'actors.detail.names.unknown', defaultMessage: 'имя неизвестно', description: 'Подпись, когда у записи имени нет значения' },
  namespaceTitle: { id: 'actors.detail.names.namespace', defaultMessage: 'Пространство имён', description: 'Подсказка по наведению на бейдж с пространством имён записи' },
  provTitle: { id: 'actors.detail.prov.title', defaultMessage: 'Происхождение', description: 'Заголовок раскрывающегося блока: строки источника, откуда взят субъект' },
  provNone: { id: 'actors.detail.prov.none', defaultMessage: 'строк источника нет', description: 'Краткая строка свёрнутого блока «Происхождение», когда строк нет' },
  provSummary: { id: 'actors.detail.prov.summary', defaultMessage: '{count, plural, one {# строка источника} few {# строки источника} many {# строк источника} other {# строки источника}}', description: 'Краткая строка свёрнутого блока «Происхождение»: сколько строк источника' },
  provMore: { id: 'actors.detail.prov.more', defaultMessage: 'показать', description: 'Кнопка раскрытия блока «Происхождение»' },
  provRest: { id: 'actors.detail.prov.rest', defaultMessage: 'и ещё {count, plural, one {# строка источника} few {# строки источника} many {# строк источника} other {# строки источника}}', description: 'Строка под первыми четырьмя записями происхождения: сколько ещё скрыто' },
});

export function ActorDetail() {
  const t = useT();
  const { actorId } = useParams();
  const context = useAnalysisContext();
  const location = useLocation();
  const id = Number(actorId);
  const request = useRequest(() => api.actor(id, context.params), [id, context.key]);
  // Две оси хозяйства собственный ресурс `/actors/{id}` не отдаёт: обязательств
  // в нём нет вовсе. Очередь проверки с фильтром по хозяйству отдаёт ровно те
  // же три знаменателя, что и на уровнях выше, — берутся они, а не считаются
  // здесь заново: второй способ посчитать «отчитались» однажды разошёлся бы с
  // первым, и заметить это можно было бы только сравнив два экрана глазами.
  const works = useRequest(
    () =>
      api.verificationQueue({
        ...context.params,
        actor_id: id,
        only_awaiting: false,
        limit: 1,
      }),
    [id, context.key],
  );

  if (request.state === "loading") {
    return <DataStatePanel state="loading" title={t(m.loading)} />;
  }
  if (request.error && !request.data) {
    return (
      <RequestErrorPanel
        error={request.error}
        title={t(m.loadFailed)}
        notFoundTitle={t(m.notFound)}
        action={<Button onClick={request.reload}>{t(COMMON.retry)}</Button>}
        // Возврат в реестр — НАВИГАЦИЯ, а не повтор: у отказа по области он
        // остаётся единственным выходом с карточки, куда человек пришёл по
        // чужой ссылке.
        navigation={
          <Link to={{ pathname: levelPath("actor"), search: context.linkSearch() }}>
            <Button>{t(m.toRegistry)}</Button>
          </Link>
        }
      />
    );
  }
  if (!request.data) return null;

  const actor = request.data;
  const identity = identityLabel(actor.identity_status);
  const scope = scopeLabel(actor.view_scope);
  // Вид субъекта — такая же подпись, как две соседние строки того же списка, а
  // не код хранилища: словарь открыт, незнакомый код называет себя.
  const kind = actorTypeLabel(actor.actor_type);

  // Массив в цепочке: он есть, когда вся земля хозяйства лежит в одном
  // массиве, — тогда ступень ведёт на его уровень. При нескольких массивах
  // ступень называет их число и НЕ ведёт никуда: выбрать один за человека
  // значило бы соврать про землю остальных. Массив каждого землепользования
  // назван ссылкой в таблице ниже в любом случае.
  const massifs = actorMassifs(actor.land_uses);
  const load = actorMassifLoad(actor.land_uses);
  const sole = massifs.length === 1 ? massifs[0] : undefined;
  const massifStep: TrailStep[] = sole
    ? [
        {
          level: "massif",
          label: sole.canonical_name,
          to: {
            pathname: levelPath("massif", sole.id),
            search: context.linkSearch(),
          },
        },
      ]
    : massifs.length > 1
      ? [
          {
            level: "massif",
            caption: t(m.massifsCaption),
            label: String(massifs.length),
            to: null,
          },
        ]
      : [];

  return (
    <div className={styles.stack}>
      <PageHeader
        title={actor.name ?? t(m.unnamed, { id: actor.id })}
        breadcrumb={
          <LevelTrail
            steps={[
              districtStep(context.linkSearch()),
              ...massifStep,
              {
                level: "actor",
                label: actor.name ?? t(m.unnamed, { id: actor.id }),
                to: null,
              },
            ]}
          />
        }
        purpose={actor.identity_note}
        actions={
          <>
            <Link to={{ pathname: levelPath("actor"), search: location.search }}>
              <Button>{t(m.toRegistry)}</Button>
            </Link>
            <Link
              to={{
                pathname: ROUTES.farmWorks,
                search: context.linkSearch({ actor_id: actor.id }),
              }}
            >
              <Button>{t(m.actionWaits)}</Button>
            </Link>
            <Link
              to={{
                pathname: ROUTES.map,
                search: context.linkSearch({ actor_id: actor.id }),
              }}
            >
              {/* Карта наводит фокус на землю хозяйства, но соседей не
                  прячет: серверного фильтра «контуры одного хозяйства» у
                  карты нет, а кадр без соседей читался бы как «вокруг
                  ничего нет». Карта говорит это сама. */}
              <Button>{t(m.actionMap)}</Button>
            </Link>
            <Link
              to={{
                pathname: ROUTES.verification,
                search: context.linkSearch({ actor_id: actor.id }),
              }}
            >
              <Button variant="primary">{t(m.actionVerify)}</Button>
            </Link>
          </>
        }
      />

      {/* Чипы массивов под цепочкой: земля хозяйства лежит в нескольких
          территориях, и цепочка уровней может назвать только одну. Каждый чип
          ведёт на уровень своей территории и несёт то, чем она для хозяйства
          является: сколько здесь контуров и сколько земли. */}
      {load.length > 0 ? (
        <div className={styles.stackTight}>
          <span className={styles.answerLabel}>{t(m.whereLand)}</span>
          <div className={styles.rowWrap}>
            {load.map((item) => (
              <Link
                key={item.massif.id}
                to={{
                  pathname: levelPath("massif", item.massif.id),
                  search: context.linkSearch(),
                }}
              >
                {item.missingRows > 0
                  ? t(m.chipMissing, {
                      massif: item.massif.canonical_name,
                      contours: item.contours,
                      area: formatArea(item.totalMicroha, { rounded: true }).text,
                      missing: item.missingRows,
                    })
                  : t(m.chip, {
                      massif: item.massif.canonical_name,
                      contours: item.contours,
                      area: formatArea(item.totalMicroha, { rounded: true }).text,
                    })}
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {/* --- Ответ уровня: что сделано, а что нет ----------------------- */}

      <Card title={t(m.doneTitle)} large>
        <DoneSplit
          counts={works.data?.obligations ?? null}
          noun={nounForms(WORDS.obligation)}
          size="hero"
          absent={
            works.state === "loading"
              ? t(m.absentLoading)
              : (works.data?.empty_note ?? t(m.absentNone))
          }
        />
        {works.data && works.data.obligations.total > 0 ? <AxisLegend /> : null}

        {/* Четыре числа уровня хозяйства и ни одним больше: земля и контуры —
            это хозяйство как земля, обязательства и «без отчёта сейчас» — оно
            же как адресат работ. Те же величины стояли вторым экземпляром в
            «Идентичности»: два места для одного числа однажды разошлись бы, и
            заметить это можно было бы только сравнив их глазами. */}
        <div className={styles.answerSecondary}>
          <div>
            <span className={styles.answerLabel}>{t(m.land)}</span>
            <Area microha={actor.total_microha} rounded />
            <p className={styles.answerNote}>
              {t(m.landArable)} <Area microha={actor.arable_microha} rounded />
            </p>
          </div>
          <div>
            <span className={styles.answerLabel}>{t(m.contours)}</span>
            <Count value={actor.contours} />
            <p className={styles.answerNote}>
              {t(m.landUsesCount, { count: formatCount(actor.land_uses.length).text })}
            </p>
          </div>
          <div>
            <span className={styles.answerLabel}>{t(m.works)}</span>
            <Count value={works.data ? works.data.obligations.total : null} />
            <p className={styles.answerNote}>
              {works.data
                ? t(m.worksAssigned)
                : works.state === "loading"
                  ? t(m.absentLoading)
                  : t(m.worksFailed)}
            </p>
          </div>
          <div>
            <span className={styles.answerLabel}>{t(m.noReport)}</span>
            <Count
              value={
                works.data
                  ? works.data.obligations.total -
                    works.data.obligations.reported
                  : null
              }
            />
            <p className={styles.answerNote}>
              {t(m.noReportNote)}
            </p>
          </div>
        </div>

        {works.data && works.data.obligations.total > 0 ? (
          <Disclosure
            tone="inline"
            title={t(m.contoursAndHa)}
            /* Величина берётся носителем `Measured`, а не устаревшим скаляром:
               вместе с ней приезжают класс доказательства и число строк без
               величины — без них одно и то же «12» на разных экранах означало
               бы разное. */
            summary={
              <>
                {works.data.awaiting.value === null
                  ? t(m.awaitingUnknown)
                  : t(m.awaiting, { count: works.data.awaiting.value })}
                {works.data.awaiting.missing_rows > 0
                  ? t(m.awaitingMissing, {
                      missing: works.data.awaiting.missing_rows,
                      rows: works.data.awaiting.rows,
                    })
                  : ""}
                {works.data.awaiting.epistemic ? (
                  <> <EvidenceBadge status={works.data.awaiting.epistemic} /></>
                ) : null}
              </>
            }
          >
            {/* Знаменатель «контуры работ» — не тот же, что «контуры земли»
                выше: сюда попадают только контуры, которых коснулись работы. */}
            <div className={styles.answerSecondary}>
              <div>
                <span className={styles.answerLabel}>{t(m.contoursWithWorks)}</span>
                <DoneSplit
                  counts={works.data.contours}
                  noun={nounForms(WORDS.contour)}
                />
              </div>
              <div>
                <AreaProgress area={works.data.area} rounded />
              </div>
            </div>
            <p className={styles.answerNote}>
              {t(m.worksNote)}
            </p>
          </Disclosure>
        ) : null}
      </Card>

      <div className={styles.detailGrid}>
        <div className={styles.stack}>
          <Card title={t(m.directionsTitle)}>
            {actor.directions.length === 0 ? (
              <DataStatePanel state="empty" title={t(m.directionsEmptyTitle)}>
                {t(m.directionsEmpty)}
              </DataStatePanel>
            ) : (
              <div className={styles.scrollX}>
                <table className={styles.plainTable}>
                  <caption>{t(m.directionsCaption)}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t(m.colAtomCode)}</th>
                      <th scope="col">{t(m.colSourceLabel)}</th>
                      <th scope="col">{t(m.colLayer)}</th>
                      <th scope="col" className={styles.num}>
                        {t(m.colAsserted)}
                      </th>
                      <th scope="col">{t(m.colClass)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actor.directions.map((direction, index) => (
                      <tr key={`${direction.direction_code}-${index}`}>
                        <td>
                          <strong>{direction.direction_code}</strong>
                          {direction.crop_code &&
                          direction.crop_code !== direction.direction_code ? (
                            <Badge>{t(m.cropBadge, { crop: direction.crop_code })}</Badge>
                          ) : null}
                        </td>
                        <td>
                          {direction.raw_label ? (
                            <span>«{direction.raw_label}»</span>
                          ) : (
                            <span>—</span>
                          )}
                        </td>
                        <td>{direction.source_layer || "—"}</td>
                        <td className={styles.num}>
                          <Count value={direction.assertions} />
                        </td>
                        <td>
                          <EvidenceBadge status={direction.epistemic_status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Землепользования — подробность уровня, а не его ответ: строк у
              крупного хозяйства десятки, и первым взглядом они вытесняют то,
              ради чего карточку открывают. Свёрнутый блок называет своё число,
              поэтому перемещение вглубь не является удалением. */}
          <Disclosure
            title={t(m.landUsesTitle)}
            summary={
              actor.land_uses.length === 0
                ? t(m.landUsesNone)
                : t(m.landUsesSummary, {
                    rows: actor.land_uses.length,
                    massifs: load.length,
                  })
            }
            more={
              actor.land_uses.length > 0
                ? t(m.landUsesMore, { count: actor.land_uses.length })
                : undefined
            }
          >
            {actor.land_uses.length === 0 ? (
              <DataStatePanel state="empty" title={t(m.landUsesEmptyTitle)}>
                {t(m.landUsesEmpty)}
              </DataStatePanel>
            ) : (
              <div className={styles.scrollX}>
                <table className={styles.plainTable}>
                  <caption>{t(m.landUsesCaption)}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t(m.colContour)}</th>
                      <th scope="col">{t(m.colMassif)}</th>
                      <th scope="col">{t(m.colPart)}</th>
                      <th scope="col" className={styles.num}>
                        {t(m.colArable)}
                      </th>
                      <th scope="col" className={styles.num}>
                        {t(m.colTotal)}
                      </th>
                      <th scope="col">{t(m.colClass)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actor.land_uses.map((use) => (
                      <tr key={use.id}>
                        <td>
                          <Link
                            to={{
                              pathname: levelPath("contour", use.contour_id),
                              search: context.linkSearch(),
                            }}
                          >
                            {use.contour_no}
                          </Link>
                        </td>
                        <td>
                          {/* Массив — адресуемый уровень: из карточки
                              хозяйства можно подняться на территорию, где
                              лежит этот контур. */}
                          <Link
                            to={{
                              pathname: levelPath("massif", use.massif.id),
                              search: context.linkSearch(),
                            }}
                          >
                            {use.massif.canonical_name}
                          </Link>
                        </td>
                        <td>{use.part_label ?? t(m.wholeContour)}</td>
                        <td className={styles.num}>
                          <Area microha={use.arable_microha} unit={null} />
                        </td>
                        <td className={styles.num}>
                          <Area microha={use.total_microha} unit={null} />
                        </td>
                        <td>
                          <EvidenceBadge status={use.epistemic_status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Disclosure>

          {actor.placements.map((block) => (
            <Card key={block.season} title={t(m.placementTitle, { season: block.season })}>
              <InlineAlert title={t(m.nonAdditive)}>
                {block.non_additive_reason}
              </InlineAlert>
              <div className={styles.scrollX}>
                <table className={styles.plainTable}>
                  <caption>{t(m.placementCaption)}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t(m.colMetric)}</th>
                      <th scope="col" className={styles.num}>
                        {t(m.colAreaHa)}
                      </th>
                      <th scope="col">{t(m.colClass)}</th>
                      <th scope="col">{t(m.colSource)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row) =>
                      row.measures.map((measure) => (
                        <tr key={`${row.id}-${measure.metric_code}`}>
                          <td>{measure.metric_code}</td>
                          <td className={styles.num}>
                            <Area microha={measure.area_microha} unit={null} />
                          </td>
                          <td>
                            <EvidenceBadge status={row.epistemic_status} />
                          </td>
                          <td>
                            <ProvenanceLine source={row.source} />
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}

          <DemoCropTimeline
            blocks={actor.crop_timeline}
            axis="actor"
            linkSearch={context.linkSearch()}
          />

          <DemoPlacementDetail
            blocks={actor.placement_details}
            axis="actor"
            linkSearch={context.linkSearch()}
          />

          {actor.placements.length === 0 ? (
            <Card title={t(m.placementCard)}>
              <DataStatePanel state="empty" title={t(m.placementEmptyTitle)}>
                {t(m.placementEmpty)}
              </DataStatePanel>
            </Card>
          ) : null}
        </div>

        <div className={styles.stack}>
          <Card title={t(m.identityTitle)}>
            <DefinitionList>
              <dt>{t(m.identityKind)}</dt>
              <dd>
                <Badge tone={kind.known ? "neutral" : "unknown"}>
                  {kind.label}
                </Badge>
              </dd>
              <dt>{t(m.identityStatus)}</dt>
              <dd>
                <Badge tone={identity.known ? "neutral" : "unknown"}>
                  {identity.label}
                </Badge>
              </dd>
              <dt>{t(m.identityScope)}</dt>
              <dd>
                <Badge tone={scope.known ? "neutral" : "unknown"}>{scope.label}</Badge>
              </dd>
              <dt>{t(m.farmLike)}</dt>
              <dd>{actor.farm_like ? t(m.yes) : t(m.no)}</dd>
            </DefinitionList>
          </Card>

          <Card title={t(m.namesTitle, { count: actor.names.length })}>
            <div className={styles.stackTight}>
              {actor.names.map((name) => (
                <div key={name.id} className={styles.stackTight}>
                  <div className={styles.rowWrap}>
                    <strong>
                      <Text value={name.value} fallback={t(m.nameUnknown)} />
                    </strong>
                    <Badge title={t(m.namespaceTitle)}>{name.namespace}</Badge>
                    <EvidenceBadge status={name.epistemic_status} />
                  </div>
                  <ProvenanceLine source={name.source} />
                </div>
              ))}
            </div>
          </Card>

          {/* Сырой JSON источника — только в раскрытии: он доказательство, а
              не первый взгляд уровня. */}
          <Disclosure
            title={t(m.provTitle)}
            summary={
              actor.provenance.length === 0
                ? t(m.provNone)
                : t(m.provSummary, { count: actor.provenance.length })
            }
            more={actor.provenance.length > 0 ? t(m.provMore) : undefined}
          >
            <div className={styles.stackTight}>
              {actor.provenance.slice(0, 4).map((record) => (
                <div key={record.source_record_id} className={styles.stackTight}>
                  <ProvenanceLine source={record} />
                  <pre className={styles.sourceBlock}>
                    {JSON.stringify(record.raw, null, 1)}
                  </pre>
                </div>
              ))}
              {actor.provenance.length > 4 ? (
                <span>{t(m.provRest, { count: actor.provenance.length - 4 })}</span>
              ) : null}
            </div>
          </Disclosure>
        </div>
      </div>

      {/* Вопрос об этом хозяйстве — свёрнутым и последним: первый взгляд
          уровня уже израсходован на две оси и землю. */}
      <LevelPocket level="actor" objectId={id} />
    </div>
  );
}
